import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import { Search, Link2, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";

const AboutUs = () => {
  React.useEffect(() => {
    document.title = "About EMU | EMU Mentoring";
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <PageHeader 
        title="About Us"
        subtitle="Learn more about EMU Mentoring and our mission."
      />
      
      {/* Main Content */}
      <div className="bg-white">
        {/* Hero Section */}
        <section className="py-16 px-4 md:px-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-emu-text mb-6">
              We Make Mentoring Comfortable, Accessible, and Powerful
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              At EMU, we believe every question deserves to be asked. Our platform connects experienced mentors 
              with ambitious individuals who are ready to learn, grow and thrive without fear of judgement. 
              We normalise asking "stupid" questions to fuel real learning and genuine success.
            </p>
          </div>
        </section>

        {/* Mission Section with Image */}
        <section className="py-16 px-4 md:px-10 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-emu-text mb-6">Our Mission</h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Our mission is to empower meaningful mentorships by removing barriers, 
                  encouraging curiosity, and supporting professional development across all industries.
                </p>
              </div>
              <div className="relative rounded-xl overflow-hidden shadow-elevated">
                <img 
                  src="/lovable-uploads/dd159ee3-d5ff-46f5-a7b0-2f4dab995044.png"
                  alt="EMU Mentoring Logo"
                  className="w-full h-full object-contain p-8 aspect-[4/3]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16 px-4 md:px-10 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-emu-text mb-6 text-center">How EMU Works</h2>
            <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-12">
              EMU simplifies the mentoring journey. Find a mentor, ask questions, schedule sessions 
              and track your growth, all within one easy-to-use platform.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              {/* Step 1 */}
              <Card className="p-6 text-center hover:shadow-lg transition-shadow duration-300">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 bg-emu-blue/10 rounded-full flex items-center justify-center">
                    <Search className="h-6 w-6 text-emu-blue" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Find Mentor</h3>
                <p className="text-gray-600">
                  Browse our network of experienced mentors and find the perfect match for your goals.
                </p>
              </Card>

              {/* Step 2 */}
              <Card className="p-6 text-center hover:shadow-lg transition-shadow duration-300">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 bg-emu-blue/10 rounded-full flex items-center justify-center">
                    <Link2 className="h-6 w-6 text-emu-blue" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Connect</h3>
                <p className="text-gray-600">
                  Schedule sessions and communicate directly with your chosen mentor.
                </p>
              </Card>

              {/* Step 3 */}
              <Card className="p-6 text-center hover:shadow-lg transition-shadow duration-300">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 bg-emu-blue/10 rounded-full flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-emu-blue" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-3">Grow</h3>
                <p className="text-gray-600">
                  Track your progress and achieve your professional development goals.
                </p>
              </Card>
            </div>
          </div>
        </section>
      </div>
      
      <Footer />
    </div>
  );
};

export default AboutUs;
